<template>
  <teleport
    to="#headerPageContent"
  >
    <HeaderBarItem>
      <UpdateReferenceDropdown
        v-model="updateReference.model"
        :schema="updateReference.schema"
        :name="updateReferenceLabel"
      />
    </HeaderBarItem>
  </teleport>

  <div :class="$style.page">
    <BaseContainer>
      <NavigationCardsGrid>
        <NavigationCard
          v-if="isAdmin || user.company.enabledFeatures.includes('analytics')"
          color="barley"
          :to="{ name: 'Analytics' }"
        >
          <template #image>
            <CardAnalyticsSvg />
          </template>
          <template #title>
            Analytics
          </template>
          <template #lead>
            Uncover cars and LDV insights with our data-rich visual tools.
          </template>
        </NavigationCard>
        <NavigationCard
          v-if="isAdmin || user.company.enabledFeatures.includes('dashboards')"
          color="lime"
          :to="{ name: 'Dashboards' }"
        >
          <template #image>
            <CardDashboardSvg />
          </template>
          <template #title>
            Dashboards
          </template>
          <template #lead>
            Monitor key insights with personalised dashboards.
          </template>
        </NavigationCard>
        <NavigationCard
          v-if="isAdmin || user.company.enabledFeatures.includes('products')"
          color="aqua"
          :to="{ name: 'Products' }"
        >
          <template #image>
            <CardProductsSvg />
          </template>
          <template #title>
            Products
          </template>
          <template #lead>
            Explore car and device data and how they're put together.
          </template>
        </NavigationCard>
      </NavigationCardsGrid>
    </BaseContainer>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import gql from 'graphql-tag';
import BaseContainer from '@/components/base/Container';
import NavigationCardsGrid from '@/components/NavigationCardsGrid';
import NavigationCard from '@/components/NavigationCard';
import UpdateReferenceDropdown from '@/components/UpdateReferenceDropdown';
import HeaderBarItem from '@/components/HeaderBarItem';
import CardAnalyticsSvg from '@/assets/images/icons/card-analytics.svg?inline';
import CardDashboardSvg from '@/assets/images/icons/card-dashboard.svg?inline';
import CardProductsSvg from '@/assets/images/icons/card-products.svg?inline';

export default {
  components: {
    BaseContainer,
    NavigationCardsGrid,
    NavigationCard,
    UpdateReferenceDropdown,
    HeaderBarItem,
    CardAnalyticsSvg,
    CardDashboardSvg,
    CardProductsSvg,
  },
  data() {
    return {
      updateReference: [],
    };
  },
  computed: {
    ...mapGetters('user', ['isAdmin']),
    ...mapState('user', ['user']),
    ...mapState('app', ['updateReferenceId']),
    ...mapState('app', ['updateReferenceLabel']),
  },
  watch: {
    'updateReference.model': {
      deep: false,
      handler: async function updateReferenceChanged(newValue) {
        this.$store.dispatch('app/referenceId', { updateReferenceId: newValue });
        const { data } = await this.$apollo.query({
          query: gql`query {
            carsSopEop: cars_sop_eop(
              where: { update_ref_id_${this.user.company.uuid.replace(/-/g, '')}: { _eq: "${newValue}", }, },
              distinct_on: car_id
            ) {
              car_id
            }
          }`,
        });
        const selectedCars = data.carsSopEop.map((item) => item.car_id);
        this.$store.dispatch('app/selectedCars', { carsId: selectedCars });
        this.$apollo.mutate({
          mutation: gql`mutation ($userId: String!, $update_ref_id: String!, $selected_cars: String!) {
            update_users_by_pk(pk_columns: {
              id: $userId}, 
              _set: {
                update_ref_id: $update_ref_id,
                selected_cars: $selected_cars
              }
            ) {
              id
            }
          }`,
          variables: {
            userId: this.user.username,
            update_ref_id: newValue,
            selected_cars: JSON.stringify(selectedCars),
          },
        });
      },
    },
  },
  async created() {
    this.updateReference = await this.loadUpdateReference(this.user);
    if (this.updateReferenceId) {
      this.updateReference.model = this.updateReferenceId;
    }
  },
  methods: {
    async loadUpdateReference(user) {
      const { data } = await this.$apollo.query({
        query: gql`query {
          importHistory: import_history(
            where: { company_id: { _eq: "${user.company.uuid.replace(/-/g, '')}", }, },
            distinct_on: uuid
          ) {
            update_ref
            uuid
          }
        }`,
      });
      return {
        schema: data.importHistory.map((x) => ({ label: x.update_ref, value: x.uuid })),
        model: data.importHistory.map((x) => x.uuid)[data.importHistory.length - 1],
      };
    },
  },
};
</script>

<style lang="scss" module>
.page {
  padding-top: map-get($page-vertical-paddings, 'regular');
  padding-bottom: map-get($page-vertical-paddings, 'regular');
}

.selectContainer {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.select {
  min-width: 200px;
}
</style>
